import { useEffect, useMemo, useState } from "react"
// Imported from the submodule instead of the package root because
// react-use-websocket is CJS-only and exposes the hook via `exports.default`,
// which Vite 8's Node-consistent CJS interop no longer unwraps.
import { useWebSocket } from "react-use-websocket/dist/lib/use-websocket"

import { SchakoAPIClient } from "shared/api/http-clients/SchakoAPIClient"
import { useAuth } from "shared/lib/useAuth"
import { useBooleanState } from "shared/lib/useBooleanState"
import { useEvent } from "shared/lib/useEvent"
import { WebsocketConnectionState } from "shared/types/websocket/WebsocketConnectionState"

interface UseProjectCollaborationWebsocketArgs {
  onMessage?: (event: WebSocketEventMap["message"]) => void
  onOpen?: (event: WebSocketEventMap["open"]) => void
  onClose?: (event: WebSocketEventMap["close"]) => void
  onError?: (event: WebSocketEventMap["error"]) => void
}

/**
 * This hook manages a WebSocket connection for real-time project collaboration features.
 * It handles authentication, connection state management, and provides methods for
 * subscribing/unsubscribing to project updates.
 *
 * ## Authentication Flow:
 * 1. Token is retrieved from SchakoAPIClient during initial connection setup
 * 2. Token is passed as a query parameter during WebSocket handshake
 * 3. Once connected, the token cannot be updated without reconnecting
 * 4. On connection errors, attempts to refresh token and relies on retryOnError
 *
 * ## Connection Logic:
 * - Only connects when: user is authenticated AND token exists AND shouldConnect is true
 * - Automatically retries on connection failures (retryOnError: true)
 * - Provides manual connect/disconnect controls
 *
 * ## Message Format:
 * - Subscribe: { type: "project.subscribe", project: { id: projectID } }
 * - Subscribe with ShareID: { type: "project.subscribe", project: { id }, shareLink: { id } }
 * - Unsubscribe: { action: "project.unsubscribe", project: { id: projectID } }
 *
 *
 * ## Error Handling:
 * - Connection errors are logged and trigger token refresh attempts
 * - Operations on closed connections are logged as warnings
 * - Automatic retry is enabled for network failures
 *
 * @param args - Optional configuration object
 * @param args.onMessage - Callback fired when WebSocket receives a message
 *
 * @returns Object with connection state and control methods
 */

export function useProjectCollaborationWebsocket(
  args?: UseProjectCollaborationWebsocketArgs
) {
  const { isAuthenticated } = useAuth()
  const { onMessage, onOpen, onClose, onError } = args || {}
  const {
    value: shouldConnect,
    setTrue: connect,
    setFalse: disconnect
  } = useBooleanState(true)
  const [token, setToken] = useState<string | null>(null)
  useEffect(() => {
    setToken(SchakoAPIClient.token || null)
  }, [isAuthenticated])

  const { readyState, sendMessage } = useWebSocket(
    `${import.meta.env.VITE_PROJECT_COLLABORATION_WEBSOCKET}`,
    {
      queryParams: { accessToken: token as string },
      onOpen: event => {
        onOpen?.(event)
      },
      onClose: event => {
        onClose?.(event)
      },
      onMessage: event => {
        onMessage?.(event)
      },
      onError: event => {
        setToken(SchakoAPIClient.token || null)
        onError?.(event)
      },
      retryOnError: true
    },
    shouldConnect && isAuthenticated && !!token
  )

  const connectionState = useMemo(() => {
    switch (readyState) {
      case 0:
        return WebsocketConnectionState.CONNECTING
      case 1:
        return WebsocketConnectionState.OPEN
      case 2:
        return WebsocketConnectionState.CLOSING
      case 3:
        return WebsocketConnectionState.CLOSED
      default:
        return WebsocketConnectionState.UNINSTANTIATED
    }
  }, [readyState])

  const unsubscribe = useEvent((projectID: string) => {
    const message = JSON.stringify({
      action: "project.unsubscribe",
      project: { id: projectID }
    })
    sendMessage(message)
  })

  const subscribe = useEvent((projectID: string) => {
    const message = JSON.stringify({
      action: "project.subscribe",
      project: { id: projectID }
    })
    sendMessage(message)
  })

  const subscribeWithShareID = useEvent(
    (projectID: string, shareID: string) => {
      const message = JSON.stringify({
        action: "project.subscribe",
        project: { id: projectID },
        shareLink: { id: shareID }
      })

      sendMessage(message)
    }
  )

  const handleConnect = useEvent(() => {
    setToken(SchakoAPIClient.token || null)
    connect()
  })

  return {
    connectionState,
    connect: handleConnect,
    disconnect,
    subscribe,
    subscribeWithShareID,
    unsubscribe
  }
}
